// La barra lateral con las conversaciones, como en el prototipo.
//
// La lista sale del servidor. Borrar aquí llama a DELETE del hilo: si no se
// puede quitar, el chat se llena y deja de servir para volver a una.
//
// **La confirmación es la propia fila, no un diálogo.** Borrar no se puede
// deshacer -el servidor no guarda una papelera-, así que pedir un segundo
// gesto está justificado; interrumpir con un modal para una fila de una lista,
// no. La fila se convierte en «¿Borrar? / Cancelar» y quien se equivocó de
// icono sale sin haber perdido nada.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Plus, PanelLeftClose, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import { RotuloDeSeccion } from '@/components/ui/rotulo-de-seccion'
import { byDayGroup } from '@/lib/day-groups'

export interface Conversacion {
  id: string
  titulo: string
  /** When it was last touched: it decides the day group it is shown under. */
  updatedAt: string
  /** The Brain is still working on it: the row shows it spinning. */
  thinking?: boolean
  /** Which step of its plan it is at, for work of several steps. */
  step?: { n: number; total: number }
}

// Hasta el 2026-09-26 toda la lista iba bajo un «Hoy» fijo, también lo de
// hace un mes.
const GROUP_LABEL = {
  today: 'brain.group_today',
  yesterday: 'brain.group_yesterday',
  week: 'brain.group_week',
  earlier: 'brain.group_previous',
} as const

interface Props {
  conversaciones: Conversacion[]
  activa: string
  onElegir: (id: string) => void
  onNueva: () => void
  onBorrar: (id: string) => void
  onOcultar: () => void
}

export function ListaDeConversaciones({
  conversaciones,
  activa,
  onElegir,
  onNueva,
  onBorrar,
  onOcultar,
}: Props) {
  const { t } = useTranslation()
  // Cuál está preguntando si se borra. Una sola: abrir la segunda cierra la
  // primera, que es lo que evita dejar media lista armada sin querer.
  const [porBorrar, setPorBorrar] = useState<string | null>(null)

  return (
    <div className="flex h-full w-64 flex-col">
      <div className="px-3 pt-3">
        <Button size="sm"
          variant="outline"
          onClick={onNueva}
          className="w-full justify-start gap-2 hover:bg-muted/60"
        >
          <Plus className="h-4 w-4" /> {t('brain.new_chat')}
        </Button>
      </div>

      <ScrollArea className="min-h-0 flex-1">
        <div className="box-border w-64 px-3 py-3">
          {byDayGroup(conversaciones, (c) => c.updatedAt).map(({ group, items }) => (
            <section key={group} className="mb-4 last:mb-0" aria-label={t(GROUP_LABEL[group])}>
              <RotuloDeSeccion className="mb-1.5 px-3">
                {t(GROUP_LABEL[group])}
              </RotuloDeSeccion>
              <div className="space-y-1">
                {items.map((c) =>
                  porBorrar === c.id ? (
                    <div
                      key={c.id}
                      className="rounded-lg bg-destructive/10 px-3 py-2 text-xs leading-tight"
                    >
                      <p className="text-destructive">{t('brain.borrar_confirmar')}</p>
                      <div className="mt-1.5 flex gap-2">
                        <Button
                          variant="link"
                          onClick={() => {
                            setPorBorrar(null)
                            onBorrar(c.id)
                          }}
                          className="font-medium text-destructive"
                        >
                          {t('brain.borrar_si')}
                        </Button>
                        <Button
                          variant="link"
                          onClick={() => setPorBorrar(null)}
                          className="text-muted-foreground"
                        >
                          {t('brain.borrar_no')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div key={c.id} className="group relative">
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => onElegir(c.id)}
                        className={cn(
                          'w-full justify-start truncate text-left leading-tight',
                          activa === c.id
                            ? 'bg-muted font-medium text-foreground'
                            : 'text-muted-foreground',
                        )}
                      >
                        {c.thinking && (
                          <Loader2
                            className="h-3.5 w-3.5 shrink-0 animate-spin text-primary"
                            aria-label={t('brain.thinking')}
                          />
                        )}
                        {c.thinking && c.step && (
                          <span
                            className="shrink-0 text-xs tabular-nums text-muted-foreground"
                            title={t('brain.plan.paso', c.step)}
                          >
                            {c.step.n}/{c.step.total}
                          </span>
                        )}
                        <span className="truncate pr-6">{c.titulo || t('brain.conversacion_sin_titulo')}</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        onClick={() => setPorBorrar(c.id)}
                        title={t('brain.borrar_conversacion')}
                        aria-label={t('brain.borrar_conversacion')}
                        className={cn(
                          'absolute right-1 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-destructive',
                          'opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100',
                        )}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ),
                )}
              </div>
            </section>
          ))}
          <p className="mt-4 px-3 text-xs leading-snug text-muted-foreground">
            {t('brain.historial_compartido')}
          </p>
        </div>
      </ScrollArea>

      <div className="border-t border-border px-3 py-2">
        <Button size="xs"
          variant="ghost"
          onClick={onOcultar}
          className="-ml-2 justify-start gap-2 rounded-lg text-muted-foreground hover:text-foreground"
        >
          <PanelLeftClose className="h-3.5 w-3.5" />
          {t('brain.hide_sidebar')}
        </Button>
      </div>
    </div>
  )
}
